import KybMonitoringRepository from "@/core/kyb/repositories/KybMonitoringRepository";
import {
  KybMonitoringCase,
  KybMonitoringIndicator,
  KybMonitoringIndicatorState,
  KybMonitoringSection,
  KybMonitoringSectionCode,
} from "@/core/kyb/entities/KybMonitoring";
import { RepositoryResult } from "@/core/shared/types/RepositoryResult";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import { executeKybRequest } from "../../http/services/kybRequest/kybRequest";
import kybMonitoringEndpoints from "../../api/kybMonitoringEndpoints/kybMonitoringEndpoints";

type IndicatorDto = {
  code: string;
  label: string;
  definition: string;
  value?: number | null;
  unit: string;
  state: KybMonitoringIndicatorState;
  note?: string | null;
  drillDown: boolean;
};

type SectionDto = {
  section: KybMonitoringSectionCode;
  generatedAt: string;
  realtimeNotice: string;
  degraded: boolean;
  indicators: IndicatorDto[];
  total?: number | null;
  totalLabel?: string | null;
};

type CasesDto = {
  indicator: string;
  caseIds: string[];
  cases?: { caseId: string; businessKey?: string | null; legalEntityName?: string | null }[] | null;
};

/** Le back rend `null` pour une valeur absente : l'écran travaille avec des champs optionnels. */
const orUndefined = <T>(value: T | null | undefined): T | undefined => value ?? undefined;

const mapIndicator = (dto: IndicatorDto): KybMonitoringIndicator => ({
  code: dto.code,
  label: dto.label,
  definition: dto.definition,
  value: orUndefined(dto.value),
  unit: dto.unit,
  state: dto.state,
  note: orUndefined(dto.note),
  drillDown: dto.drillDown,
});

const mapSection = (dto: SectionDto): KybMonitoringSection => ({
  section: dto.section,
  generatedAt: dto.generatedAt,
  realtimeNotice: dto.realtimeNotice,
  degraded: dto.degraded,
  indicators: dto.indicators.map(mapIndicator),
  total: orUndefined(dto.total),
  totalLabel: dto.totalLabel ?? "",
});

/** Les dossiers nommés quand le back les nomme ; à défaut (ancien back), seulement leurs identifiants. */
const mapCases = (dto: CasesDto): KybMonitoringCase[] =>
  dto.cases
    ? dto.cases.map((item) => ({
        caseId: item.caseId,
        businessKey: orUndefined(item.businessKey),
        legalEntityName: orUndefined(item.legalEntityName),
      }))
    : dto.caseIds.map((caseId) => ({ caseId }));

export default class KybMonitoringRepositoryBackend implements KybMonitoringRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async getSection(section: KybMonitoringSectionCode): Promise<RepositoryResult<KybMonitoringSection>> {
    return executeKybRequest(
      () => this.httpClient.get<ApiResponse<SectionDto>>({ url: kybMonitoringEndpoints.section(section), withAuth: true }),
      mapSection,
    );
  }

  async listCases(indicator: string, limit: number, offset: number): Promise<RepositoryResult<KybMonitoringCase[]>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<CasesDto>>({
          url: kybMonitoringEndpoints.cases(indicator, limit, offset),
          withAuth: true,
        }),
      mapCases,
    );
  }
}
